import React, {useEffect, useState} from 'react';

import './Program.create.scss';

import {Link, Navigate} from "react-router-dom";
import Input from "../../forms/inputs/Input";
import TextArea from "../../forms/inputs/TextArea";
import Select from "../../forms/inputs/Select";
import Checkbox from "../../forms/inputs/Checkboxk";
import Radio from "../../forms/inputs/Radio";
import FormValidation from "../../forms/validation/formValidation";
import Button from "../../forms/buttons/Button";
import {useLazyQuery, useMutation, useQuery} from "@apollo/client";
import gqlSchemaLibrary from "../../services/graphql.schemas";
import {programTemplates} from "../../visuals/ModalProgramCreate";
import {clearGqlTypename} from "../../utils/clearTypename";
import DropdownMultipleList from "../../forms/dropdown/dropdown.multipleList";
import DropdownMultiSelect from "../../forms/dropdown/dropdown.multiselect";
import {PageViewLog} from "../../services/interactions/page.view";

const programFields = {
    'name': { type: 'text', required: true, value: ''},
    'programType': { type: 'text', required: true, value: ''},
    'description': { type: 'text', required: true, value: '' },
    'duration': { type: 'text', required: true, value: null },
    'supervisorClinician': { type: 'text', required: true, value: null },
    'legalRequired': { type: 'bool', required: false, value: false },
    'populationParams': { type: 'array-items', required: false, value: [] },
    'includeCriteria': { type: 'array-items', required: false, value: [] },
    'excludeCriteria': { type: 'array-items', required: false, value: [] }
};

const inclusionCriteria = [
    "Access to Internet",
    "Access to EHR Portal",
    "Access to video visit",
    "Compliant to video visit",
    "Age > 18",
    "Hypertension",
    "Diabetes 2",
    "COPD",
    "Asthma",
    "Heart Failure",
    "Moderate-to-Severe disease burden",
    "COVID-19, mild-to-moderate"
];

const exclusionCriteria = [
    "Acute kidney injury (AKI) >CKD manifesting as a 50% or greater increase in serum creatinine",
    "AMS",
    "Aspiration PNA",
    "Asthma",
    "Cellulitis with purulent drainage or abscess",
    "Concurrent Transient Ischemic Attack (TIA) or cerebrovascular accident (CVA)",
    "Chronic Obstructive Pulmonary Disease",
    "DKA with HCO3 <14, pH<7.30",
    "Dyspnea, exercise tolerance, wheezes are extreme with an assessed very real risk of respiratory failure due to deconditioning , an/ or fatigue",
    "Evidence of acute respiratory failure (increased PCO2, lowered pH)",
    "Evidence of sepsis (see Appendix A: Sepsis Criteria)",
    "Evidence of severe sepsis (elevated lactate, low systolic blood pressure, systolic BP less than 90 mmHg], elevated procalcitonin [2X], pH <7.3)",
    "Evidence of VT, multifocal PVCs, second- or third-degree AV block",
    "Evidence to suggest post-renal obstruction as the cause of the UTI",
    "Evidence to suggest sepsis",
    "HCAP source of PNA",
    "Heart failure, decompensated",
    "Hypertensive emergency: Severe hypertension usually with systolic >180 mmHg, diastolic >115 mmHg",
    "ICU criteria",
    "Inability to maintain O2 saturation >90% on 4L O2 nasal",
    "Inability to maintain O2 saturation >92% on 4L O2 nasal (or 90% for patients with COPD)",
    "Inadequate or deleterious home situation",
    "Lung abscess",
    "MRSA history or MRSA risk",
    "New onset CHF",
    "Oliguria",
    "Oxygen saturation <90% on 4LO2 (nasal)",
    "pH <7.3",
    "Potassium >6.0 mEq/L or <3.0 mEq/L",
    "Severe sepsis",
    "Significant AMS persistence after fluid resuscitation",
    "Sodium >150 mEq/L or <128 mEq/L",
    "Stage 5 CKD with the potential need for imminent dialysis",
    "Suspected MDR bacterial etiology",
    "Temperature <36°C or >39°C",
    "Underlying conditions such as tuberculosis",
    "Underlying conditions such as tuberculosis, dysphagia, dysarthria, or cystic fibrosis",
    "Unstable or deleterious home situation",
    "WBC <4000, platelets <100,000",
    "Inability to maintain O2 saturation >93% on room air"
];


function ProgramCreate({ match, currentUser, toaster }) {
    const [programData, setProgramData] = useState({});
    const [programEscalations, setProgramEscalations] = useState([]);
    const [program, setProgram] = useState(null);
    const [programOptions, setProgramOptions] = useState([]);
    const [clinicianOptions, setClinicianOptions] = useState([]);
    const [durationOptions, setDurationOptions] = useState([]);
    const [includeCriteriaOptions, setIncludeCriteriaOptions] = useState([]);
    const [exclusionCriteriaOptions, setExclusionCriteriaOptions] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [isEditting, setIsEditting] = useState(false);

    const [dataFields, setDataFields] = useState(programFields);
    const [formValid, setFormValid] = useState(false);

    const [__submitting, __setSubmitting] = useState(false);

    useEffect(() => {
        const onFormChange = (key, value, field, valid) => {
            let p = programData;
            let v = value;
            if (key === 'duration') {
                v = value? parseInt(value): value;
            }
            p[key] = v;
            setProgramData(p);
            setDataFields(prevState => ({
                ...prevState,
                [key]: field
            }));
            setFormValid(valid);
        }

        let form = FormValidation(programFields, onFormChange);
        if (!!match.params && match.params.programID && !!program) {
            let duration = null;
            if (program.duration) {
                duration = ''+(program.duration / 1000 / 60 / 60 / 24);
            }
            form.setValue('name', program.name);
            form.setValue('programType', program.programType? program.programType : 'clinical');
            form.setValue('description', program.description? program.description : '');
            form.setValue('duration', duration);
            form.setValue('supervisorClinician', program.supervisorClinician? program.supervisorClinician : null);
            form.setValue('legalRequired', program.legalRequired? program.legalRequired : false);
            form.setValue('populationParams', program.populationParams? program.populationParams : []);
            form.setValue('includeCriteria', program.includeCriteria? program.includeCriteria : []);
            form.setValue('excludeCriteria', program.excludeCriteria? program.excludeCriteria : []);
        } else {
            form.setValue('programType', 'clinical');
            // load data from template
            if (match.params.templateID) {
                const template = programTemplates.find( d => ''+d.id === match.params.templateID);
                if (template) {
                    let programName = template.name;
                    const countProgram = programOptions.filter((p) => p.name.toLowerCase().search(template.name.toLowerCase()) !== -1).length;
                    if (countProgram > 0) {
                        programName = ' Copy '+programName+' '+countProgram;
                    }
                    form.setValue('name', programName);
                    form.setValue('duration', ''+template.duration);
                    setSelectedTemplate(template);
                }
            }
        }
        setDataFields(form.data);
        const days = [];
        for (let i = 1; i <= 30; i++){
            days.push({
               label: ""+i,
               value: ""+i
            });
        }
        setDurationOptions(days);
    }, [programFields, program, match, programOptions])

    useEffect(() => {
        const include = inclusionCriteria.map((v) => {
            return {label: v, value: v}
        });
        setIncludeCriteriaOptions(include);
        const exclude = exclusionCriteria.map((v) => {
            return {label: v, value: v}
        });
        setExclusionCriteriaOptions(exclude);
    }, [inclusionCriteria, exclusionCriteria])

    const loadClinicianOptions = (usersClinical)  => {
        let items = [];
        usersClinical.forEach((user) => {
            if(user.role === "clinician" || user.role === 'Admin') {
                items.push({
                    label: user.user.bio.firstname + " " + user.user.bio.lastname,
                    value: user._id
                });
            }
        });
        setClinicianOptions(items);
    };

    useQuery(gqlSchemaLibrary.clinicians.queries.getCliniciansByOrg, {
        onCompleted: ({getCliniciansByOrg}) => {
            console.log('[User List] User Clinicians: ', getCliniciansByOrg);
            if (getCliniciansByOrg) {
                loadClinicianOptions(getCliniciansByOrg);
            }
        },
        fetchPolicy: 'cache-and-network'
    })

    useQuery(gqlSchemaLibrary.programs.query.getCurrentOrgPrograms, {
        onCompleted: (onPrograms) => {
            if (onPrograms.getCurrentOrgPrograms) {
                console.log('onPrograms.getCurrentOrgPrograms: ', onPrograms.getCurrentOrgPrograms);
                setProgramOptions(onPrograms.getCurrentOrgPrograms);
            }
        },
        fetchPolicy: 'cache-and-network'
    })

    const [getProgramEdit] = useLazyQuery(gqlSchemaLibrary.programs.query.getProgramById, {
        onCompleted: ({ getProgramById }) => {
            console.log('Program to edit: ', getProgramById);
            // Update current inputs to edit patient, disable inputs where necessary
            setProgram(clearGqlTypename(getProgramById));
        },
        fetchPolicy: 'cache-and-network'
    })

    const [getProgramEscalations] = useLazyQuery(gqlSchemaLibrary.programs.query.getProgramEscalations, {
        onCompleted: ({ getEscalationRoutineByProgramId }) => {
            //setProgramEscalations(getEscalationRoutineByProgramId);
        },
        fetchPolicy: 'cache-and-network'
    })

    const [updateCurrentProgram] = useMutation(gqlSchemaLibrary.programs.mutation.editProgram, {
        onCompleted: (onUpdated) => {
            console.log('Program updated: ', onUpdated.editPatient);
            Navigate(`/programs`);
        }
    });

    const [createProgram] = useMutation(gqlSchemaLibrary.programs.mutation.declareNewProgram, {
        onCompleted: ({declareNewProgram}) => {
            console.log('create program: ', declareNewProgram);
            Navigate(`/programs`);
        }
    })

    // Check if this is an edit call
    useEffect(() => {
        if (!!match.params && match.params.programID) {
            if(!currentUser.userRoles.programsBase.edit) {
                cancelProgram();
            }
            setIsEditting(true);
            getProgramEdit({
                variables: {
                    id: match.params.programID
                }
            })
            getProgramEscalations({
                variables: {
                    programId:  match.params.programID
                }
            })
        } else {
            if(!currentUser.userRoles.programsBase.create) {
                cancelProgram();
            }
        }
    }, [match, getProgramEdit]);

    const validateAndSubmit = () => {
        if (!currentUser.userRoles.programsBase.edit && match.params.programID) {
            return;
        } else if (!currentUser.userRoles.programsBase.create) {
            return;
        }
        if (!formValid || __submitting) {
            return; // Not valid yet or submitting
        }
        console.log("[Program Data]", programData);
        __setSubmitting(true);
        // days to milliseconds
        let _programData = Object.assign({}, programData);
        _programData.duration = programData.duration * 1000 * 60 * 60 * 24;
        if (isEditting || (!!match.params && match.params.programID)) {
            if (!program) {
                console.error('No program!');
                __setSubmitting(false);
                return;
            }
            _programData._id = program._id;
            updateCurrentProgram({
                variables: {
                    program: _programData,
                }
            })
        } else {
            let vitals;
            if (selectedTemplate) {
                vitals = selectedTemplate.vitals;
            } else {
                vitals = [
                    {
                        label: 'Heart Rate',
                        value: 'HR',
                        order: 1
                    },
                    {
                        label: 'Respiration Rate',
                        value: 'RR',
                        order: 2
                    },
                    {
                        label: 'Sp02',
                        value: 'SPO2',
                        order: 3
                    },
                    {
                        label: 'Temperature',
                        value: 'Temp',
                        order: 3
                    }
                ];
            }
            _programData.ecgEnabled = false;
            _programData.vitals = vitals;
            _programData.ignoreSustained = [];
            _programData.ignoreAcute = [];
            createProgram({
                variables: {
                    program: _programData,
                }
            })
        }
    }

    const cancelProgram = () => {
        Navigate(`/programs`);
    }

    const populationOptions = [
        {
            key: 'Age Group',
            value: [
                'Any', '18 +'
            ]
        },
        {
            key: 'Gender',
            value: [
                'Any', 'Male', 'Female', 'Intersex', 'Trans-MTF', 'Trans-FTM', 'Fluid'
            ]
        },
        {
            key: 'Race',
            value: [
                'Any', 'White', 'Black', 'Asian', 'Pacific-Islander', 'Native-American', 'Black or Indigenous'
            ]
        },
        {
            key: 'Ethnicity',
            value: [
                'Any', 'Non-Hispanic', 'Hispanic'
            ]
        },
        {
            key: 'Housing Status',
            value: [
                'Any', 'Rent', 'Own', 'Dependent', 'Shelter', 'Homeless', 'Any Non-Homeless'
            ]
        },
        {
            key: 'Smoking Status',
            value: [
                'Any', 'Nonsmoker', 'Smoker < 10', 'Smoker 10-20', 'Smoker > 20'
            ]
        }
    ]

    const unacknowledged = (e) => {
        if (e.cronString !== '0 */1 * * * *' || !e.vitalsRules.length) {
            return null;
        }
        return e.vitalsRules.map((v) => {
            if (!!v.gte) {
                return (v.gte/1000/60);
            }
            return null;
        })[0];
    }

    return (
        <div id="ProgramCreate">
            <div className="filter">
                <div className="programOptions">
                    <i className="material-icons">
                        <Link to="/programs">
                            arrow_back_ios
                    </Link>
                    </i>
                    <div className="title">
                        <h3>{ program? program.name : 'New Program' }</h3>
                    </div>
                </div>
            </div>
            <div className="formContent" >
                <div className="title">
                    <p>
                        General information
                    </p>
                </div>
                <div className="table scrollBar">
                    <div className="row">
                        <span className="label" >Program name</span>
                        <Input required={dataFields['name'].required} value={dataFields['name'].value} onChange={dataFields['name'].onChange} />
                    </div>
                    <div className="row">
                        <span className="label" >Type</span>
                        <div className="radiosContainer">
                            <Radio name="programType" label="Clinical" value="clinical" selectedValue={dataFields['programType'].value} onChange={dataFields['programType'].onChange} />
                            <Radio name="programType" label="Not clinical" value="not-clinical" selectedValue={dataFields['programType'].value} onChange={dataFields['programType'].onChange} />
                        </div>
                    </div>
                    <div className="row">
                        <span className="label" >Description</span>
                        <TextArea required={dataFields['description'].required} value={dataFields['description'].value} onChange={dataFields['description'].onChange} />
                    </div>
                    <div className="row">
                        <span className="label" >Duration</span>
                        <div className="selectContent">
                            <Select required={false} value={dataFields['duration'].value} onChange={dataFields['duration'].onChange} placeholder="--" options={durationOptions}/>
                            <span>Days</span>
                        </div>
                    </div>
                    <div className="row">
                        <span className="label" >Program Manager</span>
                        <Select value={dataFields['supervisorClinician'].value} label="" onChange={dataFields['supervisorClinician'].onChange} placeholder={'--'} options={clinicianOptions} />
                    </div>
                    <div className="row">
                        <span className="label" >Legal requirements</span>
                        <Checkbox label="Upload patients consents" onChange={dataFields['legalRequired'].onChange} bool={dataFields['legalRequired'].value} />
                    </div>
                    <div className="row">
                        <span className="label" >
                        </span>
                        <h3 className="labelDivider">Population</h3>
                    </div>
                    <div className="row">
                        <span className="label" >Parameters</span>
                        <DropdownMultipleList items={dataFields['populationParams'].value} options={populationOptions} onChange={dataFields['populationParams'].onChange} />
                    </div>
                    <div className="row">
                        <span className="label" >
                        </span>
                        <h3 className="labelDivider">Inclusion criteria</h3>
                    </div>
                    <div className="row">
                        <span className="label" >
                        </span>
                        <div className="items">
                            <div className="tags">
                                {
                                    dataFields['includeCriteria'].value.map((item, index) => (
                                        <span key={index}>{item}{ index < (dataFields['includeCriteria'].value.length-1)? ',\xa0' : '' }</span>
                                    ))
                                }
                            </div>
                            <DropdownMultiSelect label={"All that apply"} items={dataFields['includeCriteria'].value} options={includeCriteriaOptions} onChange={dataFields['includeCriteria'].onChange} />
                        </div>
                    </div>
                    <div className="row">
                        <span className="label" >
                        </span>
                        <h3 className="labelDivider">Exclusion criteria</h3>
                    </div>
                    <div className="row">
                        <span className="label" >
                        </span>
                        <div className="items">
                            <div className="tags">
                                {
                                    dataFields['excludeCriteria'].value.map((item, index) => (
                                        <span key={index}>{item}{ index < (dataFields['excludeCriteria'].value.length-1)? ',\xa0' : '' }</span>
                                    ))
                                }
                            </div>
                            <DropdownMultiSelect label={"All that apply"} items={dataFields['excludeCriteria'].value} options={exclusionCriteriaOptions} onChange={dataFields['excludeCriteria'].onChange} />
                        </div>
                    </div>

                    {
                        programEscalations.length?
                            <div className="row rowEscalations">
                                <p><b>Escalations ({programEscalations.length})</b></p>
                                {
                                    programEscalations.map((e, i) => (
                                        <div className="item">
                                            <span><b>CronJob</b></span>
                                            <span>{e.cronString}</span>
                                            <div>
                                                {e.vitalsRules.map((v) => (
                                                    <span> <b>{v.vital}</b></span>
                                                ))}
                                            </div>

                                            {
                                                unacknowledged(e)?
                                                    <>
                                                        <span><b>Type</b></span>
                                                        <span><b>Unacknowledged {unacknowledged(e)} Min</b></span>
                                                        <span/>
                                                    </> : ''
                                            }

                                            <span><b>TimeZone</b></span>
                                            <span>{e.timeZone}</span>
                                            <span/>
                                            {
                                                e.noData? (
                                                    <>
                                                        <span><b>Type</b></span>
                                                        <span><b>No Data Received</b></span>
                                                        <span/>
                                                    </>
                                                ) : ''
                                            }
                                            {
                                                e.bioReport? (
                                                    <>
                                                        <span><b>Type</b></span>
                                                        <span><b>Send Bio Report</b></span>
                                                        <span/>
                                                    </>
                                                ) : ''
                                            }

                                            <span><b>ActionsToTake</b></span>
                                            <span/>
                                            <span/>
                                            {
                                                e.actionsToTake.map((v) => (
                                                    <>
                                                        <span><b>{v.action}</b></span>
                                                        <span><b>{v.whom}</b></span>
                                                        <span><b>{v.template}</b></span>
                                                    </>
                                                ))
                                            }
                                        </div>
                                    ))
                                }
                            </div> : ''
                    }
                </div>
                <div className="footerContent">
                    <Button className="button link" handleClick={cancelProgram}>Cancel</Button>
                    <Button disabled={!formValid} className="primary smallPad" handleClick={validateAndSubmit}>
                        <div className={`progressButton ${__submitting ? 'submitting' : ''}`}>
                            {__submitting ? <div className="_progressing"></div> : ''}
                            <span>Save</span>
                        </div>
                    </Button>
                </div>
            </div>
        </div>
    );
}

export default PageViewLog(ProgramCreate);
